import {
  CalendarClock,
  CircleDollarSign,
  ClipboardList,
  Crosshair,
  ExternalLink,
  FlaskConical,
  MessageSquare,
  Stethoscope,
  TriangleAlert,
  UserRound,
} from 'lucide-react'
import { useState } from 'react'

import {
  type DateFormat,
  formatCurrency,
  formatInstantDate,
  getTaskStatusLabel,
  LAB_TYPE_LABELS,
  PROCEDURE_TYPE_LABELS,
  type ProcedureRequestData,
  TASK_TYPE_LABELS,
  type TaskResponse,
  type TaskStatus,
} from '@snootsvet/shared'

import { ExpandBadge } from '@/frontend/components/expand-badge'
import { FormattedCalendarDate, useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { TaskActions } from '@/frontend/features/tasks/task-actions'
import { TaskCommentThread } from '@/frontend/features/tasks/task-comment-thread'
import { formatAssignee, getTaskStatusVariant } from '@/frontend/features/tasks/task-utils'

/** Timeline entry card for a patient task. */
export function TaskEntry({ task }: { task: TaskResponse }) {
  const [threadOpen, setThreadOpen] = useState(false)

  return (
    <div className="overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10">
      <div className="px-4 py-3 space-y-1">
        <div className="flex items-center gap-2">
          <span className="min-w-0 truncate text-headline-sm" title={task.title}>
            {task.type === 'procedure_request' ? task.title : `Task: ${task.title}`}
          </span>
          <Badge variant={getTaskStatusVariant(task)} size="sm">
            {getTaskStatusLabel(task)}
          </Badge>
          <ExpandBadge
            open={threadOpen}
            onToggle={() => setThreadOpen(!threadOpen)}
            icon={<MessageSquare className="size-3" />}
          >
            Thread ({task.comments.length})
          </ExpandBadge>
          <div className="ml-auto -my-1 shrink-0">
            <TaskActions task={task} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <ClipboardList className="size-3 shrink-0" />
            {TASK_TYPE_LABELS[task.type]}
          </span>
          {task.data.type === 'labs' && task.data.labType && (
            <span className="flex items-center gap-1">
              <FlaskConical className="size-3 shrink-0" />
              {LAB_TYPE_LABELS[task.data.labType]}
            </span>
          )}
          {task.creator && (
            <span className="flex items-center gap-1">
              <UserRound className="size-3 shrink-0" />
              {task.creator.firstName} {task.creator.lastName}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Crosshair className="size-3 shrink-0" />
            {formatAssignee(task)}
          </span>
          {'urgent' in task.data && task.data.urgent && (
            <span className="flex items-center gap-1 text-destructive-fg">
              <TriangleAlert className="size-3 shrink-0" />
              Urgent
            </span>
          )}
          {task.data.type === 'follow_up' && task.data.dueDate && (
            <span className="flex items-center gap-1">
              <CalendarClock className="size-3 shrink-0" />
              Due <FormattedCalendarDate value={task.data.dueDate} />
            </span>
          )}
        </div>
        {task.data.type === 'procedure_request' && (
          <ProcedureRequestEntryDetails data={task.data} status={task.status} />
        )}
      </div>
      {threadOpen && (
        <div className="border-t px-4 pb-3">
          <TaskCommentThread task={task} />
        </div>
      )}
    </div>
  )
}

/** "£120.00 · Paid 3 Sep 2026", "£120.00 · Awaiting payment", "£120.00 · Waived", "£120.00 · Unpaid", or "No fee". */
export function formatProcedureFeeLine(
  data: ProcedureRequestData,
  status: TaskStatus,
  fmt: DateFormat
): string {
  if (!data.fee) return 'No fee'
  const amount = formatCurrency(data.fee.amount, data.fee.currency)
  if (data.paidAt) return `${amount} · Paid ${formatInstantDate(data.paidAt, fmt)}`
  if (status === 'open') return `${amount} · Awaiting payment`
  if (status === 'cancelled') return `${amount} · Unpaid`
  // Ready or Booked with no paidAt means the fee was waived by hand (invoice voided, task moved by the tech team).
  return `${amount} · Waived`
}

/** Procedure, fee outcome, payment page while unpaid, and the vet's note. */
function ProcedureRequestEntryDetails({
  data,
  status,
}: {
  data: ProcedureRequestData
  status: TaskStatus
}) {
  // The request isn't recorded at a clinic, so its dates follow the viewer.
  const fmt = useViewerFormat()

  return (
    <div className="space-y-2 pt-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Stethoscope className="size-3 shrink-0" />
          {PROCEDURE_TYPE_LABELS[data.procedure]}
        </span>
        <span className="flex items-center gap-1">
          <CircleDollarSign className="size-3 shrink-0" />
          {formatProcedureFeeLine(data, status, fmt)}
        </span>
        {status === 'open' && data.hostedInvoiceUrl && (
          <a
            href={data.hostedInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 underline underline-offset-2"
          >
            <ExternalLink className="size-3 shrink-0" />
            Payment page
          </a>
        )}
      </div>
      <blockquote className="whitespace-pre-wrap border-l-2 pl-3 text-sm">{data.note}</blockquote>
    </div>
  )
}
